import { Ship, Component, CompiledSystems } from '../interfaces/types';

// 1. Comparación de tamaños formato corto
const SIZE_ORDER: Record<string, number> = {
  'v': 1,
  'n': 2,
  's': 3,
  'm': 4,
  'l': 5,
  'c': 6
};

// 2. Comparación de tamaños formato largo
const FULL_SIZE_ORDER: Record<string, number> = {
  'vehicle': 1,
  'snub': 2,
  'small': 3,
  'medium': 4,
  'large': 5,
  'capital': 6
};

// 3. Comparación de números simple
const compareNumbers = (a: number, b: number): number => {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return a - b;
};

// Función para extraer el tamaño de un arma (S1, S2, etc.)
const getWeaponSize = (str: string): number => {
  const sizeRegex = /S(\d+)/i;
  const match = str.match(sizeRegex);
  const size = match ? parseInt(match[1], 10) : 0;
  return size;
};

// Función para extraer la cantidad (el número entre paréntesis)
const getWeaponCount = (str: string): number => {
  const countRegex = /x(\d+)/i;
  const match = str.match(countRegex);
  const count = match ? parseInt(match[1], 10) : 1;
  return count;
};

// 4. Comparación de armas
const compareWeapons = (weapon1: string, weapon2: string): number => {
  const w1Size = getWeaponSize(weapon1);
  const w2Size = getWeaponSize(weapon2);
  const w1Count = getWeaponCount(weapon1);
  const w2Count = getWeaponCount(weapon2);

  // Primero comparar por tamaño
  if (w1Size !== w2Size) {
    return w1Size - w2Size;
  }
  // Si tienen el mismo tamaño, comparar por cantidad
  return w1Count - w2Count;
};

// Función principal de comparación que selecciona el tipo adecuado
const compareValues = (
  value1: string | number | null, 
  value2: string | number | null, 
  type: string
): number => {
  // Convertir null a 0 para comparaciones numéricas
  const v1 = value1 ?? 0;
  const v2 = value2 ?? 0;

  // Si son números, comparación directa
  if (typeof v1 === 'number' && typeof v2 === 'number') {
    return compareNumbers(v1, v2);
  }

  const str1 = String(v1).toLowerCase();
  const str2 = String(v2).toLowerCase();

  // Si es un arma
  if (type === 'RSIWeapon_weapons') {
    return compareWeapons(str1, str2);
  }

  // Si es un tamaño en formato corto (S, M, L, C)
  const char1 = str1.charAt(0);
  const char2 = str2.charAt(0);
  if (char1 in SIZE_ORDER && char2 in SIZE_ORDER) {
    return SIZE_ORDER[char1] - SIZE_ORDER[char2];
  }

  // Si es un tamaño en formato largo (small, medium, large, capital)
  if (str1 in FULL_SIZE_ORDER && str2 in FULL_SIZE_ORDER) {
    return FULL_SIZE_ORDER[str1] - FULL_SIZE_ORDER[str2];
  }

  return 0;
};

interface ComponentComparisonItem {
  name: string;
  size: string;
  isHighest: boolean;
  isUnique: boolean;
}

interface ComparisonResult {
  value: string | number | ComponentComparisonItem[];
  isHighest: boolean;
  isUnique: boolean;
}

// Traducciones de atributos
const ATTRIBUTE_TRANSLATIONS: Record<string, string> = {
  // Atributos básicos
  'size': 'Tamaño',
  'length': 'Longitud',
  'beam': 'Anchura',
  'height': 'Altura',
  'mass': 'Masa',
  'cargo_capacity': 'Capacidad de Carga',
  'max_crew': 'Tripulación Máxima',
  'min_crew': 'Tripulación Mínima',
  'scm_speed': 'Velocidad SCM',
  'afterburner_speed': 'Velocidad Postcombustión',
  
  // Sistemas de la nave
  'RSIWeapon_weapons': 'Armas',
  'RSIWeapon_turrets': 'Torretas',
  'RSIWeapon_missiles': 'Misiles',
  'RSIPropulsion_quantum_drive': 'Motor Cuántico',
  'RSIPropulsion_fuel_intake': 'Combustible',
  'RSIPropulsion_fuel_tank': 'Tanque de Combustible',
  'RSIAvionic_computer': 'Computadoras',
  'RSIAvionic_radar': 'Radar',
  'RSIModular_cooler': 'Refrigeración',
  'RSIModular_shield_generator': 'Escudos',
  'RSIModular_power_plant': 'Energía',
  'RSIThruster_main_thruster': 'Impulsor Principal',
  'RSIThruster_maneuvering_thruster': 'Movilidad',
  'RSIUtility_utility': 'Utilidad',
  
  // Tipos de componentes individuales
  'weapons': 'Armas',
  'turrets': 'Torretas',
  'missiles': 'Misiles',
  'quantum_drive': 'Motor Cuántico',
  'fuel_intake': 'Combustible',
  'fuel_tank': 'Tanque de Combustible',
  'computer': 'Computadoras',
  'radar': 'Radar',
  'cooler': 'Refrigeración',
  'shield_generator': 'Escudos',
  'power_plant': 'Energía',
  'main_thruster': 'Impulsor Principal',
  'maneuvering_thruster': 'Movilidad',
  'utility': 'Utilidad',
  
  // Sistemas principales
  'RSIWeapon': 'Armamento',
  'RSIPropulsion': 'Propulsión',
  'RSIAvionic': 'Aviónica',
  'RSIModular': 'Módulos',
  'RSIThruster': 'Propulsores',
  'RSIUtility': 'Utilidad',

  // Términos especiales
  'jump': 'Salto',
  'quantum': 'Cuántico',
  'computers': 'Computadoras',
  'main': 'Impulsor Principal',
  'maneuvering': 'Movilidad',
  'shield': 'Escudos',
  'coolers': 'Refrigeración'
};

// Función para obtener el nombre traducido de un atributo
const getAttributeDisplayName = (attr: string): string => {
  // Si es un atributo compuesto (system_type)
  if (attr.includes('_')) {
    const [, type] = attr.split('_');
    // Si existe una traducción exacta, úsala
    if (ATTRIBUTE_TRANSLATIONS[attr]) {
      return ATTRIBUTE_TRANSLATIONS[attr];
    }
    // Si no, intenta traducir el tipo
    return ATTRIBUTE_TRANSLATIONS[type] || type;
  }
  // Si es un atributo simple
  return ATTRIBUTE_TRANSLATIONS[attr] || attr;
};


export const compareShipAttributes = (ships: Ship[]): Record<string, ComparisonResult[]> => {
  const comparison: Record<string, ComparisonResult[]> = {};

  // Atributos básicos a comparar
  const basicAttributes = [
    'size', 'length', 'beam', 'height', 'mass',
    'cargo_capacity', 'max_crew', 'min_crew',
    'scm_speed', 'afterburner_speed'
  ] as const;

  // Procesar atributos básicos
  basicAttributes.forEach(attr => {
    const displayName = getAttributeDisplayName(attr);
    comparison[displayName] = ships.map(ship => {
      const value = ship[attr];
      const values = ships.map(s => s[attr]);
      const allEqual = values.every(v => v === value);
      const isHighest = !allEqual && values.every(v => compareValues(value, v, attr) >= 0);
      
      return {
        value: value ?? 0,
        isHighest,
        isUnique: values.filter(v => v === value).length === 1
      };
    });
  });

  // Procesar componentes y armas
  ships.forEach((ship, index) => {
    if (!ship.compiled) return;

    Object.entries(ship.compiled).forEach(([system, components]) => {
      Object.entries(components as Record<string, Component[]>).forEach(([type, items]) => {
        const key = `${system}_${type}`;
        const displayName = getAttributeDisplayName(key);
        
        if (!comparison[displayName]) {
          comparison[displayName] = ships.map(() => ({
            value: [] as ComponentComparisonItem[],
            isHighest: false,
            isUnique: false
          }));
        }

        items.forEach((item: Component) => {
          if (!item.name) return;
          const size = item.size || item.component_size || '';
          
          let displaySize = size;
          if (key === 'RSIWeapon_weapons') {
            const weaponSize = getWeaponSize(size);
            const weaponCount = getWeaponCount(size);
            displaySize = `${size} -> [S:${weaponSize}, C:${weaponCount}]`;
          }

          // Comparar con componentes similares en otras naves
          const allSizes = ships.map(otherShip => {
            const otherComponents = otherShip.compiled?.[system as keyof CompiledSystems];
            if (!otherComponents) return [];
            
            const typeComponents = (otherComponents as Record<string, Component[]>)[type];
            if (!typeComponents) return [];

            return typeComponents.map(other => other.size || other.component_size || '');
          }).flat();

          const allEqual = allSizes.every(s => s === size);
          const isHighest = !allEqual && ships.every((otherShip, i) => {
            if (i === index) return true;
            const otherComponents = otherShip.compiled?.[system as keyof CompiledSystems];
            if (!otherComponents) return true;
            
            const typeComponents = (otherComponents as Record<string, Component[]>)[type];
            if (!typeComponents) return true;

            return typeComponents.every(other => 
              compareValues(size, other.size || other.component_size || '', key) >= 0
            );
          });

          // Función auxiliar para verificar si un componente existe en otra nave
          const componentExistsInShip = (s: Ship) => {
            const systemComponents = s.compiled?.[system as keyof CompiledSystems];
            if (!systemComponents) return false;
            
            const typeComponents = (systemComponents as Record<string, Component[]>)[type];
            return typeComponents?.some((c: Component) => c.name === item.name) || false;
          };

          (comparison[displayName][index].value as ComponentComparisonItem[]).push({
            name: item.name,
            size: displaySize,
            isHighest,
            isUnique: ships.every((s, i) => i === index || !componentExistsInShip(s))
          });
        });
      });
    });
  });

  return comparison;
}; 